import { Tooltip } from "@material-ui/core"
import useModal from "hooks/useModalHook"
import moment from "moment"
import RedirectModal from "pages/Roadmap/Modals/RedirectModal"
import React, { useEffect, useState } from "react"
import Loader from "react-loaders"
import { Label } from "reactstrap"
import api from "services/api.service"
import { userService } from "services/user.service"
import colors from "utils/colors"
import isNullOrEmpty, { isDateNullOrDefault } from "utils/isNullOrEmpty"

const getQuarterRange = (year, quarter) => {
  const startMonth = (quarter - 1) * 3
  const endMonth = startMonth + 2

  const start = moment(new Date(year, startMonth, 1))
  const end = moment(new Date(year, endMonth, 1)).endOf("month").endOf("day") // last day of the quarter

  return { start, end }
}

const doesOverlap = (startA, endA, startB, endB) => {
  return (
    (startB <= startA && startA <= endB) ||
    (startB <= endA && endA <= endB) ||
    (startA <= startB && startB <= endA) ||
    (startA <= endB && endB <= endA) ||
    (startA <= startB && endB <= endA) ||
    (startB <= startA && endA <= endB) ||
    (startA <= startB && endB <= endA) ||
    (startB <= startA && endA <= endB)
  )
}

// Calculate how much of a quarter is covered (0-1)
const calculateQuarterCoverage = (
  taskStart,
  taskEnd,
  quarterStart,
  quarterEnd
) => {
  // Ensure task dates are within quarter bounds for calculations
  const effectiveStart = taskStart < quarterStart ? quarterStart : taskStart
  const effectiveEnd = taskEnd > quarterEnd ? quarterEnd : taskEnd

  // Calculate total days in the quarter
  const quarterDays = (quarterEnd - quarterStart) / (1000 * 60 * 60 * 24) + 1

  // Calculate days covered by the task in this quarter
  const coveredDays =
    (effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24) + 1

  // Calculate coverage as a percentage (0-1)
  return coveredDays / quarterDays
}

// Calculate how far into the quarter the task starts (0-1)
const calculateQuarterStartPosition = (taskStart, quarterStart, quarterEnd) => {
  // If task starts before quarter, return 0
  if (taskStart <= quarterStart) return 0

  // Calculate total days in the quarter
  const quarterDays = (quarterEnd - quarterStart) / (1000 * 60 * 60 * 24) + 1

  // Calculate days from quarter start to task start
  const daysToTaskStart = (taskStart - quarterStart) / (1000 * 60 * 60 * 24)

  // Return position as percentage (0-1)
  return daysToTaskStart / quarterDays
}

// Calculate how far into the quarter the task ends (0-1)
const calculateQuarterEndPosition = (taskEnd, quarterStart, quarterEnd) => {
  // If task ends after quarter, return 1
  if (taskEnd >= quarterEnd) return 1

  // Calculate total days in the quarter
  const quarterDays = (quarterEnd - quarterStart) / (1000 * 60 * 60 * 24) + 1

  // Calculate days from quarter start to task end
  const daysToTaskEnd = (taskEnd - quarterStart) / (1000 * 60 * 60 * 24) + 1

  // Return position as percentage (0-1)
  return daysToTaskEnd / quarterDays
}

// Determine if task starts, ends, or spans the full quarter
const getQuarterCoverageType = (
  taskStart,
  taskEnd,
  quarterStart,
  quarterEnd
) => {
  const startsInQuarter = taskStart >= quarterStart && taskStart <= quarterEnd
  const endsInQuarter = taskEnd >= quarterStart && taskEnd <= quarterEnd

  if (startsInQuarter && endsInQuarter) {
    return "partial" // Both start and end in this quarter
  } else if (startsInQuarter) {
    return "starts" // Only starts in this quarter
  } else if (endsInQuarter) {
    return "ends" // Only ends in this quarter
  } else if (taskStart < quarterStart && taskEnd > quarterEnd) {
    return "full" // Spans the entire quarter
  }
  return "none"
}

const CategoryInfoGanttChart = ({ roadmapId, history }) => {
  const currentUser = userService.getLoggedInUser()
  const [categoriesWithAlternatives, setCategoriesWithAlternatives] = useState(
    []
  )
  const [roadmap, setRoadmap] = useState(null)
  const [quarterColumns, setQuarterColumns] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [quarterPixels, setQuarterPixels] = useState(50)
  const zoomIncrementInPixels = 20
  const maxQuarterPixels = 300
  const minQuarterPixels = 30
  const categoryInfoMinWidthInPixels = 150
  const categoryInfoMaxWidthInPixels = 1000
  const categoryInfoIncrementInPixels = 20

  const [currentDayPosition, setCurrentDayPosition] = useState(null)

  const [link1, setLink1] = useState(null)
  const [link2, setLink2] = useState(null)
  const { show: showRedirectModal, toggle: toggleRedirectModal } = useModal()

  useEffect(() => {
    if (!isNullOrEmpty(roadmapId)) {
      loadData()
    }
  }, [roadmapId])

  useEffect(() => {
    calculateCurrentDayPosition()
  }, [quarterColumns])

  const calculateCurrentDayPosition = () => {
    if (quarterColumns.length === 0) return

    const today = moment()

    // Find which quarter contains today
    for (let i = 0; i < quarterColumns.length; i++) {
      const { year, quarter } = quarterColumns[i]
      const { start: quarterStart, end: quarterEnd } = getQuarterRange(
        year,
        quarter
      )

      if (today >= quarterStart && today <= quarterEnd) {
        // Calculate position within this quarter (0-1)
        const quarterDays =
          (quarterEnd - quarterStart) / (1000 * 60 * 60 * 24) + 1
        const daysPassed = (today - quarterStart) / (1000 * 60 * 60 * 24)
        const positionInQuarter = daysPassed / quarterDays

        setCurrentDayPosition({
          quarterIndex: i,
          positionInQuarter,
        })
        return
      }
    }

    // If today is outside displayed quarters, don't show line
    setCurrentDayPosition(null)
  }

  const loadData = async () => {
    try {
      setIsLoading(true)
      let roadmap = await api.getRoadmap(roadmapId)
      setRoadmap(roadmap)
      let roadmapAlternatives = await api.getRoadmapAlternatives(roadmapId)
      roadmapAlternatives = roadmapAlternatives
        .filter(
          x =>
            !isNullOrEmpty(x.roadmapCategory) &&
            !isDateNullOrDefault(x.desiredStart) &&
            !isDateNullOrDefault(x.plannedFinish)
        )
        .sort((a, b) => {
          if (
            a.roadmapCategory.roadmapCategoryName !==
            b.roadmapCategory.roadmapCategoryName
          ) {
            return a.roadmapCategory.roadmapCategoryName.localeCompare(
              b.roadmapCategory.roadmapCategoryName
            )
          }

          // If categoryID is the same, sort by desiredStart date
          //return moment(a.desiredStart) - moment(b.desiredStart)
        })

      let fromYear =
        roadmapAlternatives.length == 0
          ? new Date().getFullYear()
          : Math.min(
              ...roadmapAlternatives.map(x => moment(x.desiredStart).year())
            )
      let toYear =
        roadmapAlternatives.length == 0
          ? new Date().getFullYear()
          : Math.max(
              ...roadmapAlternatives.map(x => moment(x.plannedFinish).year())
            )

      const quarterColumns = []
      for (let year = fromYear; year <= toYear; year++) {
        for (let quarter = 1; quarter <= 4; quarter++) {
          quarterColumns.push({ label: `Q${quarter} ${year}`, year, quarter })
        }
      }
      setQuarterColumns(quarterColumns)

      let categoriesWithAlternatives = []
      roadmapAlternatives.forEach((alt, index) => {
        let cat = categoriesWithAlternatives.find(
          x => x.roadmapCategoryID == alt.roadmapCategory.roadmapCategoryID
        )
        if (cat == null || cat == undefined) {
          categoriesWithAlternatives.push({
            ...alt.roadmapCategory,
            alternatives: [alt],
          })
        } else {
          cat.alternatives.push(alt)
        }
      })

      setCategoriesWithAlternatives(categoriesWithAlternatives)
      setIsLoading(false)
    } catch (err) {
      setIsLoading(false)
    }
  }

  const zoomIn = () => {
    if (quarterPixels < maxQuarterPixels) {
      setQuarterPixels(quarterPixels + zoomIncrementInPixels)
    }
  }

  const zoomOut = () => {
    if (quarterPixels > minQuarterPixels) {
      setQuarterPixels(quarterPixels - zoomIncrementInPixels)
    }
  }

  const increaseInfoWidth = () => {
    if (
      roadmap?.roadmapID > 0 &&
      roadmap.categoryInfoWidthInPixels < categoryInfoMaxWidthInPixels
    ) {
      let updated = {
        ...roadmap,
        categoryInfoWidthInPixels:
          roadmap.categoryInfoWidthInPixels + categoryInfoIncrementInPixels,
      }
      api.updateRoadmapCategoryInfoWidth(
        roadmap.roadmapID,
        updated.categoryInfoWidthInPixels
      )
      setRoadmap(updated)
    }
  }

  const decreaseInfoWidth = async () => {
    if (
      roadmap?.roadmapID > 0 &&
      roadmap.categoryInfoWidthInPixels > categoryInfoMinWidthInPixels
    ) {
      let updated = {
        ...roadmap,
        categoryInfoWidthInPixels:
          roadmap.categoryInfoWidthInPixels - categoryInfoIncrementInPixels,
      }
      setRoadmap(updated)
      await api.updateRoadmapCategoryInfoWidth(
        roadmap.roadmapID,
        updated.categoryInfoWidthInPixels
      )
    }
  }

  const handleAlternativeClick = entry => {
    if (!isNullOrEmpty(entry.link1) && !isNullOrEmpty(entry.link2)) {
      setLink1(entry.link1)
      setLink2(entry.link2)
      toggleRedirectModal()
    } else if (!isNullOrEmpty(entry.link1)) {
      //redirect
      const location = {
        pathname: entry.link1,
        state: { fromDashboard: true },
      }
      history.push(location)
    }
  }

  // Render a single alternative row with gantt chart
  const renderAlternativeRow = (entry, rowIndex) => {
    const start = moment(entry.desiredStart, "YYYY-MM-DDTHH:mm:ss")
    const end = moment(entry.plannedFinish, "YYYY-MM-DDTHH:mm:ss")

    // Find first and last active quarter
    let firstActiveQuarter = -1
    let lastActiveQuarter = -1

    // Array to store quarter coverage information
    const quarterCoverage = []

    quarterColumns.forEach((q, index) => {
      const { start: qStart, end: qEnd } = getQuarterRange(q.year, q.quarter)
      const isInQuarter = doesOverlap(start, end, qStart, qEnd)

      if (isInQuarter) {
        if (firstActiveQuarter === -1) {
          firstActiveQuarter = index
        }
        lastActiveQuarter = index

        // Calculate coverage information
        const coverageType = getQuarterCoverageType(start, end, qStart, qEnd)

        let startPosition = 0
        let endPosition = 1

        if (coverageType === "partial") {
          startPosition = calculateQuarterStartPosition(start, qStart, qEnd)
          endPosition = calculateQuarterEndPosition(end, qStart, qEnd)
        } else if (coverageType === "starts") {
          startPosition = calculateQuarterStartPosition(start, qStart, qEnd)
        } else if (coverageType === "ends") {
          endPosition = calculateQuarterEndPosition(end, qStart, qEnd)
        }

        quarterCoverage.push({
          index,
          coverageType,
          startPosition,
          endPosition,
        })
      }
    })

    // Create quarters columns
    let renderedCells = []

    // Add empty cells before the task starts
    if (firstActiveQuarter > 0) {
      for (let i = 0; i < firstActiveQuarter; i++) {
        renderedCells.push(<td key={`empty-before-${i}`}></td>)
      }
    }

    let colSpan = quarterCoverage.length

    if (colSpan > 0) {
      // Get the first and last coverage information
      const firstCoverage = quarterCoverage[0]
      const lastCoverage = quarterCoverage[quarterCoverage.length - 1]

      // Calculate scaled percentages based on colSpan
      let leftMarginPercent = 0
      let rightMarginPercent = 0

      // Left margin: only apply to first quarter cell
      if (
        firstCoverage.coverageType === "starts" ||
        firstCoverage.coverageType === "partial"
      ) {
        // Calculate what percentage of the ENTIRE task's width this margin represents
        leftMarginPercent = (firstCoverage.startPosition * 100) / colSpan
      }

      // Right margin: only apply to last quarter cell
      if (
        lastCoverage.coverageType === "ends" ||
        lastCoverage.coverageType === "partial"
      ) {
        // Calculate what percentage of the ENTIRE task's width this margin represents
        rightMarginPercent = ((1 - lastCoverage.endPosition) * 100) / colSpan
      }

      renderedCells.push(
        <td
          key="gantt-cell"
          colSpan={colSpan}
          style={{
            background: `linear-gradient(to right, 
              transparent 0%, 
              transparent ${leftMarginPercent}%, 
              ${
                entry.roadmapCategory?.color ?? "lightgreen"
              } ${leftMarginPercent}%, 
              ${entry.roadmapCategory?.color ?? "lightgreen"} ${
              100 - rightMarginPercent
            }%, 
              transparent ${100 - rightMarginPercent}%, 
              transparent 100%)`,
            position: "relative",
            padding: "4px 0",
            cursor: "pointer",
            // width: '150px'
          }}
          onClick={() => handleAlternativeClick(entry)}
        >
          <Tooltip
            PopperProps={{
              className: "gantt-tooltip",
            }}
            title={
              <div
                style={{}}
                dangerouslySetInnerHTML={{
                  __html: entry.toolTip,
                }}
              ></div>
            }
          >
            {/* <span>aaa</span> */}

            <div
              style={{
                marginLeft: `${leftMarginPercent + 1}%`,
                width: `${Math.max(
                  99 - leftMarginPercent - rightMarginPercent,
                  0
                )}%`,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                padding: 0,
                color: "white",
              }}
            >
              <div className="d-flex-row ai-center gap-3">
                {/* {!isNullOrEmpty(entry.decision) && `(${entry.decision})`}{" "} */}
                {!isNullOrEmpty(entry.iconHTML) && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: entry.iconHTML,
                    }}
                  ></div>
                )}{" "}
                <span>
                  {entry.alternativeNum} {entry.alternativeName}
                </span>
              </div>
            </div>
          </Tooltip>
        </td>
      )
    }

    // Add empty cells after the task ends
    if (lastActiveQuarter < quarterColumns.length - 1) {
      for (let i = lastActiveQuarter + 1; i < quarterColumns.length; i++) {
        renderedCells.push(<td key={`empty-after-${i}`}></td>)
      }
    }

    return renderedCells
  }

  return (
    <React.Fragment>
      {isLoading && (
        <Loader
          type="line-scale-pulse-out"
          color={colors.primary}
          style={{ textAlign: "center" }}
        />
      )}
      {!isLoading && (
        <div>
          <div className="d-flex-row ai-center gap-3 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={zoomIn}
              disabled={quarterPixels >= maxQuarterPixels}
            >
              <i className="fas fa-plus"></i> Zoom In
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={zoomOut}
              disabled={quarterPixels <= minQuarterPixels}
            >
              <i className="fas fa-minus"></i> Zoom Out
            </button>
            {roadmap != null && currentUser.userID === roadmap.userID && (
              <>
                <Label>Increase or Decrease Info column width</Label>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={increaseInfoWidth}
                  disabled={
                    roadmap.categoryInfoWidthInPixels >=
                    categoryInfoMaxWidthInPixels
                  }
                >
                  <i className="fas fa-plus"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={decreaseInfoWidth}
                  disabled={
                    roadmap.categoryInfoWidthInPixels <=
                    categoryInfoMinWidthInPixels
                  }
                >
                  <i className="fas fa-minus"></i>
                </button>
              </>
            )}
          </div>
          <div
            style={{
              // overflowX: "auto",
              width: "100%",
              position: "relative",
            }}
          >
            {/* {currentDayPosition && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: `${
                    150 +
                    (roadmap?.categoryInfoWidthInPixels ?? 0) +
                    currentDayPosition.quarterIndex * quarterPixels +
                    currentDayPosition.positionInQuarter * quarterPixels
                  }px`,
                  width: "2px",
                  backgroundColor: "red",
                  zIndex: 1000,
                  pointerEvents: "none",
                }}
              >
                 <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: "-35px",
                    backgroundColor: "red",
                    color: "white",
                    padding: "2px 5px",
                    borderRadius: "3px",
                    fontSize: "10px",
                  }}
                >
                  TODAY
                </div> 
              </div>
            )} */}
            <table
              className="table table-bordered low-padding-table"
              style={{
                backgroundColor: "white",
                tableLayout: "fixed",
                // minWidth: `${100 + 150 + quarterColumns.length * 150}px`,
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: "150px" }}></th>
                  <th
                    style={{
                      width: `${roadmap?.categoryInfoWidthInPixels ?? 0}px`,
                    }}
                  ></th>

                  {(() => {
                    const years = {}
                    quarterColumns.forEach(q => {
                      if (!years[q.year]) {
                        years[q.year] = 0
                      }
                      years[q.year]++
                    })

                    return Object.entries(years).map(([year, count]) => (
                      <th
                        key={`year-${year}`}
                        colSpan={count}
                        style={{
                          textAlign: "center",
                          width: `${quarterPixels * count}px`,
                        }}
                      >
                        {year}
                      </th>
                    ))
                  })()}
                </tr>
                <tr>
                  <th style={{ width: "100px" }}>Category</th>
                  <th
                    style={{
                      width: `${roadmap?.categoryInfoWidthInPixels ?? 0}px`,
                    }}
                  >
                    Info
                  </th>
                  {quarterColumns.map((q, index) => (
                    <th key={index} style={{ width: `${quarterPixels}px` }}>
                      Q{q.quarter}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* <thead>
            <tr>
              <th style={{ width: "100px" }}>Category</th>
              <th style={{ width: "150px" }}>Info</th>
              {quarterColumns.map((q, index) => (
                <th key={index} style={{ width: "100px" }}>
                  {q.label}
                </th>
              ))}
            </tr>
          </thead> */}
              <tbody>
                {categoriesWithAlternatives.map((category, catIndex) => {
                  const categoryHasImage = !isNullOrEmpty(
                    category.rmCategoryImagePath
                  )
                  const altCount = category.alternatives.length

                  return (
                    <React.Fragment key={`category-${catIndex}`}>
                      {/* First row with category and first alternative */}
                      {category.alternatives.map((alternative, altIndex) => {
                        if (altIndex === 0) {
                          // First alternative - include category with rowspan
                          return (
                            <tr
                              key={`alt-${catIndex}-${altIndex}`}
                              className="short-row"
                            >
                              <td
                                rowSpan={altCount + 1}
                                style={{
                                  verticalAlign: "top",
                                  padding: "8px",
                                  width: "150px",
                                }}
                              >
                                <div
                                  style={{
                                    marginBottom: categoryHasImage
                                      ? "8px"
                                      : "0",
                                    color: category.color ?? "black",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {category.roadmapCategoryName}
                                </div>

                                {categoryHasImage && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      lineHeight: "20px",
                                    }}
                                  >
                                    <img
                                      src={category.rmCategoryImagePath}
                                      alt={`${category.roadmapCategoryName} Image`}
                                      style={{
                                        maxWidth: "75px",
                                        display: "block",
                                      }}
                                    />
                                  </div>
                                )}
                              </td>
                              <td
                                rowSpan={altCount + 1}
                                style={{ overflow: "auto" }}
                              >
                                {category.rmCategoryInfo && (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: category.rmCategoryInfo,
                                    }}
                                  ></div>
                                )}
                              </td>
                              {renderAlternativeRow(alternative, altIndex)}
                            </tr>
                          )
                        }
                        // Other alternatives
                        return (
                          <tr
                            key={`alt-${catIndex}-${altIndex}`}
                            className="short-row"
                          >
                            {renderAlternativeRow(alternative, altIndex)}
                          </tr>
                        )
                      })}

                      <tr className="category-spacer">
                        <td style={{ padding: 0 }}></td>
                      </tr>
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <RedirectModal
        isOpen={showRedirectModal}
        toggleModal={toggleRedirectModal}
        history={history}
        link1={link1}
        link2={link2}
      />
    </React.Fragment>
  )
}

export default CategoryInfoGanttChart
